import { useMap, useMapsLibrary } from '@vis.gl/react-google-maps'
import PropTypes from 'prop-types'
import { useEffect, useRef, useMemo } from 'react'

const RoutePolyline = ({
  coordinates,
  strokeColor = '#1976D2',
  strokeWeight = 2,
  strokeOpacity = 0.8,
  zIndex = 1,
  showArrows = false,
  dashed = false,
  dashOffset = '0',
}) => {
  const map = useMap()
  const mapsLib = useMapsLibrary('maps')
  const polylineRef = useRef(null)

  const arrowSymbol = useMemo(() => {
    if (!showArrows || !window.google?.maps?.SymbolPath) return null
    const arrowScale = strokeWeight > 4 ? 3 : 2
    return {
      path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
      scale: arrowScale,
      strokeColor,
      fillColor: strokeColor,
      strokeWeight: 1,
      fillOpacity: strokeOpacity,
      strokeOpacity,
    }
  }, [showArrows, strokeColor, strokeWeight, strokeOpacity])

  // Google Maps has no native dashArray — the canonical technique is a
  // transparent main stroke plus a short line symbol repeated along the path.
  const dashSymbol = useMemo(() => {
    if (!dashed) return null
    return {
      path: 'M 0,-1 0,1',
      strokeOpacity,
      strokeColor,
      scale: strokeWeight,
    }
  }, [dashed, strokeColor, strokeWeight, strokeOpacity])

  const icons = useMemo(() => {
    const list = []
    if (dashSymbol) list.push({ icon: dashSymbol, offset: dashOffset, repeat: `${strokeWeight * 4}px` })
    if (arrowSymbol) list.push({ icon: arrowSymbol, offset: '50%', repeat: '120px' })
    return list.length > 0 ? list : undefined
  }, [dashSymbol, arrowSymbol, strokeWeight, dashOffset])

  useEffect(() => {
    if (!map || !mapsLib || !Array.isArray(coordinates) || coordinates.length < 2) return undefined

    // Drop non-finite coordinates (bad geocodes) — the Polyline constructor
    // throws InvalidValueError on a single NaN and the whole line vanishes.
    const path = coordinates
      .map(coord => ({
        lat: parseFloat(coord[1]),
        lng: parseFloat(coord[0]),
      }))
      .filter(point => Number.isFinite(point.lat) && Number.isFinite(point.lng))
    if (path.length < 2) return undefined

    // Dashed mode hides the continuous stroke; only the repeated symbols paint.
    const mainStrokeOpacity = dashed ? 0 : strokeOpacity

    if (!polylineRef.current) {
      polylineRef.current = new mapsLib.Polyline({
        map,
        path,
        strokeColor,
        strokeWeight,
        strokeOpacity: mainStrokeOpacity,
        zIndex,
        icons,
      })
    } else {
      polylineRef.current.setPath(path)
      polylineRef.current.setOptions({ strokeColor, strokeWeight, strokeOpacity: mainStrokeOpacity, zIndex, icons })
    }

    return () => {
      if (polylineRef.current) {
        polylineRef.current.setMap(null)
        polylineRef.current = null
      }
    }
  }, [map, mapsLib, coordinates, strokeColor, strokeWeight, strokeOpacity, zIndex, icons, dashed])

  return null
}

RoutePolyline.propTypes = {
  coordinates: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
  strokeColor: PropTypes.string,
  strokeWeight: PropTypes.number,
  strokeOpacity: PropTypes.number,
  zIndex: PropTypes.number,
  showArrows: PropTypes.bool,
  dashed: PropTypes.bool,
  dashOffset: PropTypes.string,
}

export default RoutePolyline
